// src/hooks/useConsultSuggestions.ts
//
// The live consultation suggestion engine, moved into the browser.
//
// `talk/services/suggestionEngine.js` is GONE — talk holds no AI at all any more. What it used to
// do on the server, one pass per transcript segment, this hook now does from the practitioner's
// own tab: three generation calls against `xoe-agent` (via healthcare's relay, `ConsultGenerationApi`),
// merged locally, then pushed back to talk with `setSuggestions` so talk stays the STORE and the
// ROUTER for the room. talk whole-replaces `roomInfo.suggestions` and broadcasts `suggestionUpdate`
// to every tab in the room, including this one.
//
// The orchestration below is a faithful port of `processTranscriptForSuggestions()` and its four
// helpers. The PROMPTS did not come with it — they live in
// `xoe-agent/app/generation/pipelines/consult_suggestions.py` and stay there.
//
// ── FIVE THINGS THAT FAIL SILENTLY IF THEY ARE CHANGED ────────────────────────────────────────
//
// 1. A 200 IS NOT SUCCESS. Every generation answers `200` with `metadata.status` inside it, and
//    `data` is `null` on failure — never `{}`. Each stage maps a failure back to the benign default
//    the JS had: triage -> `getDefaultTriage()` (all six analyzers false), `.generate` -> `[]` FOR
//    THAT TYPE ONLY so the other five still land, `.updates` -> `[]`. A failed pass must stay
//    invisible: the panel simply shows nothing new, exactly as it does when nothing clinically
//    notable was said. `null` must never reach the suggestion list.
//
// 2. `runKey` IS A FRESH UUID PER CALL. The agent's run ledger is keyed on
//    `(pipeline, patient_id, run_key)`; keyed on anything stable — the room, a cycle number — it
//    replays cycle 1 verbatim forever. The panel populates once and then freezes, every response
//    `status: complete`, nothing in any log.
//
// 3. THE `.updates` STAMPING. `status: "accepted"`, `respondedBy: "ai"` and `responseTimestamp`
//    were dropped from the agent's schema on purpose (they are server assertions about what the AI
//    did, not model output). We stamp all three ourselves, SPREAD FIRST so our values win.
//    `mergeSuggestions()` computes `updateTime = update.responseTimestamp || update.timestamp || 0`
//    and applies the update only `if (updateTime >= currentTime)`, where `currentTime` is the
//    pending suggestion's own timestamp — stamped at creation, always non-zero. An unstamped update
//    therefore compares `0 >= <a real ms timestamp>` and EVERY update is silently discarded: no
//    error, no log line, and a panel that never marks anything as answered.
//
// 4. `.slice(0, 3)` AND THE `text && why` FILTER STAY HERE. `maxItems: 3` in the agent's schema is
//    ADVISORY — the Anthropic API does not validate tool input against `input_schema` — so these
//    two lines are the only enforcement anywhere.
//
// 5. THE `type` OVERWRITE. `type: suggestionType` is stamped server-side after the parse, together
//    with a generated `id`, `status: 'pending'`, `suggestionRecordCreated: false` and `timestamp`.
//    Nothing downstream may depend on the model getting `type` right — that is precisely what lets
//    the six suggestion types share ONE pipeline key with `suggestion_type` as an input.
//
// ── CONCURRENCY ───────────────────────────────────────────────────────────────────────────────
// talk held a per-room lock and DROPPED a pass when one was already running rather than overlapping
// it: overlapping passes lose suggestions through read-modify-write (both read the same `existing`,
// both merge, the second write wins and the first round's new suggestions vanish). One browser does
// not remove the hazard — two timers, or a burst of transcript segments, overlap just as happily.
// `inFlightRef` is that lock, and it DROPS rather than queues, for the same reason.

import { useCallback, useRef } from "react";

import { generate, runKey } from "../../api/healthcare/Consultation/ConsultGenerationApi";
import { Transcript } from "../../types/appointments/ConsultTypes";
import { Patient } from "../../types/healthcare/PatientTypes";
import { Suggestion, SuggestionType } from "../../utils/healthcare/SugestionUtils";
import type { UserUuid } from "../../types/identity";

// ── The three pipelines ───────────────────────────────────────────────────────────────────────
// SIX SUGGESTION TYPES, ONE KEY: `suggestion_type` is an INPUT to `.generate`, never a key suffix.
export const TRIAGE_PIPELINE = "consult_suggestions.triage";
export const GENERATE_PIPELINE = "consult_suggestions.generate";
export const UPDATES_PIPELINE = "consult_suggestions.updates";

export const TRIAGE_TIMEOUT_MS = 60_000;
export const GENERATE_TIMEOUT_MS = 60_000;
export const UPDATES_TIMEOUT_MS = 60_000;

// ── The bounds, ported verbatim from `suggestionEngine.js:10-14` ──────────────────────────────
export const MAX_TRANSCRIPTS_FOR_CONTEXT = 12;
export const MIN_TRANSCRIPTS_FOR_SUGGESTIONS = 3;
export const TRIAGE_COOLDOWN = 12_000;
export const MAX_SUGGESTIONS_PER_TYPE = 5;
/** `.slice(0, 3)` at `suggestionEngine.js:315`. The agent's `maxItems: 3` is advisory. */
export const MAX_SUGGESTIONS_PER_RESPONSE = 3;
/** `checkSuggestionUpdates` capped its own transcript context tighter than the round did. */
export const MAX_TRANSCRIPTS_FOR_UPDATES = 10;

/**
 * `suggestionTypes` (`suggestionEngine.js:606-613`), in the order the round fans them out.
 * NOT the order of the triage prompt's analyzer list — two orders for the same six concepts.
 */
export const SUGGESTION_TYPES: SuggestionType[] = ["alert", "diagnosis", "investigation", "treatment", "question", "referral"];

export interface TriageResult {
	run_analyzers: Record<string, boolean>;
	priority_concerns?: string[];
	reasoning?: string;
}

export interface ConsultSuggestionsRound {
	suggestions: Suggestion[];
	hasChanges: boolean;
}

/** `getDefaultTriage()` (`suggestionEngine.js:193-206`) — the fail-soft, all six analyzers false. */
export function getDefaultTriage(): TriageResult {
	return {
		run_analyzers: {
			question_suggestions: false,
			investigation_suggestions: false,
			diagnosis_suggestions: false,
			treatment_suggestions: false,
			alert_suggestions: false,
			referral_suggestions: false,
		},
		priority_concerns: [],
		reasoning: "Default triage",
	};
}

/**
 * `getRelevantTranscripts()` (`suggestionEngine.js:96-106`).
 *
 * ⚠️ DESCENDING by timestamp, then capped — so the model has always read these consults BACKWARDS.
 * Ported as-is: re-ordering here would change what the model sees on every live consult, which is a
 * clinical-quality change and belongs to whoever measures it. The agent does not re-sort.
 */
export function getRelevantTranscripts(transcripts: Transcript[], maxCount: number = MAX_TRANSCRIPTS_FOR_CONTEXT): Transcript[] {
	if (!Array.isArray(transcripts)) return [];

	const sorted = [...transcripts].sort((a, b) => (b?.timestamp || 0) - (a?.timestamp || 0));

	return sorted.slice(0, maxCount);
}

/**
 * `mergeSuggestions()` (`suggestionEngine.js:411-470`) — id-keyed, and it never loses a suggestion.
 *
 * Three steps, in this order and no other:
 *   1. every EXISTING suggestion goes in;
 *   2. UPDATES are applied over them, but only when the update is at least as new as what is there;
 *   3. NEW suggestions are added only when their id is not already present.
 */
export function mergeSuggestions(
	existing: Suggestion[] | null | undefined,
	updates: Suggestion[] | null | undefined,
	newSuggestions: Suggestion[] | null | undefined,
): Suggestion[] {
	const merged = new Map<string, Suggestion>();

	// Step 1 — ALL existing suggestions.
	(existing || []).forEach((s) => {
		if (s?.id) merged.set(s.id, { ...s });
	});

	// Step 2 — apply updates to existing suggestions.
	(updates || []).forEach((update) => {
		if (!update?.id) return;

		const current = merged.get(update.id);
		if (current) {
			// ⚠️ THE COMPARISON THE `.updates` STAMPING EXISTS FOR. `currentTime` is the pending
			// suggestion's own creation timestamp and is always non-zero, so an UNSTAMPED update
			// compares `0 >= <a real ms timestamp>` and is dropped in silence.
			const updateTime = update.responseTimestamp || update.timestamp || 0;
			const currentTime = current.responseTimestamp || current.timestamp || 0;

			if (updateTime >= currentTime) {
				merged.set(update.id, {
					...current,
					...update,
					// The two fields an update may never overwrite.
					id: current.id,
					type: current.type || update.type,
				});
			}
		} else {
			// Should not happen; the model invented an id. Added anyway — dropping it here would
			// hide the only signal anyone has that it is doing so.
			merged.set(update.id, { ...update });
		}
	});

	// Step 3 — NEW suggestions, only when the id is free.
	(newSuggestions || []).forEach((newSug) => {
		if (newSug?.id && !merged.has(newSug.id)) {
			merged.set(newSug.id, { ...newSug });
		}
	});

	return Array.from(merged.values());
}

/** A generation answered successfully AND carried a payload. `data` is `null` on every failure. */
function completed<T>(envelope: { data: T | null; metadata: { status: string } } | null | undefined): T | null {
	if (!envelope || envelope.metadata?.status !== "complete") return null;
	return envelope.data ?? null;
}

interface StageArgs {
	patient: Patient | null;
	patientId: UserUuid | null;
	roomId: string;
}

/**
 * `generateTriage()` (`suggestionEngine.js:124-188`) — which analyzers are worth running.
 *
 * `transcripts` arrives already sorted newest-first and capped at 12; the further `.slice(0, 10)`
 * this call used to apply moved into the pipeline with the prompt.
 */
export async function generateTriage(
	args: StageArgs & { transcripts: Transcript[]; activeSuggestions: Suggestion[] },
): Promise<TriageResult> {
	try {
		const envelope = await generate<TriageResult>({
			pipeline: TRIAGE_PIPELINE,
			runKey: runKey(),
			patientId: args.patientId,
			inputs: {
				patient: args.patient,
				transcripts: args.transcripts,
				active_suggestions: args.activeSuggestions || [],
			},
			timeoutMs: TRIAGE_TIMEOUT_MS,
		});

		const data = completed<TriageResult>(envelope);
		// Event name + room id only — the response is model output conditioned on a named patient's
		// live consultation, and it must never reach a console the way a payload log would.
		if (!data) {
			console.warn("consult.suggestions.triage.unavailable", { roomId: args.roomId });
			return getDefaultTriage();
		}

		if (!data.run_analyzers || typeof data.run_analyzers !== "object") return getDefaultTriage();

		return data;
	} catch {
		console.error("consult.suggestions.triage.failed", { roomId: args.roomId });
		return getDefaultTriage();
	}
}

/**
 * `generateSpecializedSuggestions()` (`suggestionEngine.js:211-326`) — one suggestion type.
 *
 * ⚠️ THE `MAX_SUGGESTIONS_PER_TYPE` EARLY RETURN SHORT-CIRCUITS BEFORE ANY HTTP CALL. It is the
 * only thing bounding how many suggestions of one type a consult can accumulate, and moving it
 * below the request would pay for a generation whose result is then thrown away.
 */
export async function generateSpecializedSuggestions(
	args: StageArgs & {
		transcripts: Transcript[];
		existingSuggestions: Suggestion[];
		suggestionType: SuggestionType;
		consultationNotes?: string;
		consultParticulars?: string;
	},
): Promise<Suggestion[]> {
	const { suggestionType } = args;

	try {
		const relevantTranscripts = getRelevantTranscripts(args.transcripts, MAX_TRANSCRIPTS_FOR_CONTEXT);

		const typeSpecificSuggestions = (args.existingSuggestions || []).filter((s) => s?.type === suggestionType && s?.status !== "deleted");

		if (typeSpecificSuggestions.length >= MAX_SUGGESTIONS_PER_TYPE) return [];

		const envelope = await generate<Suggestion[]>({
			pipeline: GENERATE_PIPELINE,
			runKey: runKey(),
			patientId: args.patientId,
			inputs: {
				patient: args.patient,
				transcripts: relevantTranscripts,
				// The room's whole set — the pipeline filters it to this type and projects it.
				existing_suggestions: args.existingSuggestions || [],
				suggestion_type: suggestionType,
				consultation_notes: args.consultationNotes || "",
				consult_particulars: args.consultParticulars || "",
			},
			timeoutMs: GENERATE_TIMEOUT_MS,
		});

		const data = completed<Suggestion[]>(envelope);
		// `[]` FOR THIS TYPE ONLY — the other five fan-out branches are unaffected.
		if (!Array.isArray(data)) return [];

		const stampedAt = Date.now();

		return data
			.filter((s) => s?.text && s?.why)
			.map(
				(s) =>
					({
						...s,
						id: `${suggestionType}-${stampedAt}-${Math.random().toString(36).substring(2, 9)}`,
						status: "pending",
						type: suggestionType,
						suggestionRecordCreated: false,
						timestamp: stampedAt,
					}) as Suggestion,
			)
			.slice(0, MAX_SUGGESTIONS_PER_RESPONSE);
	} catch {
		console.error("consult.suggestions.generate.failed", { roomId: args.roomId, suggestionType });
		return [];
	}
}

/**
 * `checkSuggestionUpdates()` (`suggestionEngine.js:331-406`) — which pending suggestions the
 * practitioner has already addressed out loud.
 *
 * The `pendingSuggestions.length === 0` early return short-circuits before any HTTP call, and the
 * three server-owned fields are stamped onto every returned update BEFORE it can reach a merge.
 */
export async function checkSuggestionUpdates(
	args: StageArgs & { transcripts: Transcript[]; existingSuggestions: Suggestion[] },
): Promise<Suggestion[]> {
	try {
		const relevantTranscripts = getRelevantTranscripts(args.transcripts, MAX_TRANSCRIPTS_FOR_UPDATES);

		const pendingSuggestions = (args.existingSuggestions || []).filter((s) => s?.status === "pending" && !s?.suggestionRecordCreated);

		if (pendingSuggestions.length === 0) return [];

		const envelope = await generate<Suggestion[]>({
			pipeline: UPDATES_PIPELINE,
			runKey: runKey(),
			patientId: args.patientId,
			inputs: {
				patient: args.patient,
				transcripts: relevantTranscripts,
				// The room's whole set — the pipeline filters it to pending-and-not-yet-recorded.
				pending_suggestions: args.existingSuggestions || [],
			},
			timeoutMs: UPDATES_TIMEOUT_MS,
		});

		const data = completed<Suggestion[]>(envelope);
		if (!Array.isArray(data)) return [];

		const respondedAt = Date.now();

		// ⚠️ SPREAD FIRST, STAMPS LAST. See note 3 at the top of this file — without
		// `responseTimestamp` in particular, `mergeSuggestions()` discards every one of these and
		// says nothing about it.
		return data
			.filter((u) => u && typeof u === "object")
			.map(
				(u) =>
					({
						...u,
						status: "accepted",
						respondedBy: "ai",
						responseTimestamp: respondedAt,
					}) as Suggestion,
			);
	} catch {
		console.error("consult.suggestions.updates.failed", { roomId: args.roomId });
		return [];
	}
}

export interface UseConsultSuggestionsOptions {
	/** The room the cooldown is keyed on. Two consults must never share one 12-second window. */
	roomId: string;
	patient: Patient | null;
	transcripts: Transcript[];
	/** The room's current suggestion set — whatever talk last broadcast. */
	suggestions: Suggestion[];
	consultationNotes?: string;
	consultParticulars?: string;
	/** AI analysis armed, and this is a live consult rather than a completed record. */
	enabled?: boolean;
}

export interface UseConsultSuggestionsResult {
	/**
	 * Run one full pass. Resolves with the merged set, or `null` when nothing ran — the gates, the
	 * in-flight drop and a missing patient all answer `null` and no HTTP call is made.
	 */
	runRound: () => Promise<ConsultSuggestionsRound | null>;
	/** True while a pass is in flight. For rendering only; the guard itself reads a ref. */
	isRunning: () => boolean;
}

export function useConsultSuggestions(options: UseConsultSuggestionsOptions): UseConsultSuggestionsResult {
	// Every live value goes through a ref so `runRound` keeps ONE identity for the life of the
	// component. Both consult pages register their socket handlers in an effect that runs once, and
	// a callback that changed identity per render would be captured stale there.
	const optionsRef = useRef(options);
	optionsRef.current = options;

	// The triage cooldown, per hook instance, keyed by room. It held the room's LAST TRIAGE TIME,
	// never a cached response — the pipelines are deliberately not cacheable.
	const triageCacheRef = useRef<Map<string, number>>(new Map());

	const inFlightRef = useRef<boolean>(false);

	const isRunning = useCallback(() => inFlightRef.current, []);

	/** `shouldRunTriage()` (`suggestionEngine.js:111-119`). */
	const shouldRunTriage = useCallback((roomId: string): boolean => {
		const lastTriage = triageCacheRef.current.get(roomId);
		if (!lastTriage) return true;

		return Date.now() - lastTriage > TRIAGE_COOLDOWN;
	}, []);

	const runRound = useCallback(async (): Promise<ConsultSuggestionsRound | null> => {
		const input = optionsRef.current;
		const roomId = input.roomId || "unknown";

		if (input.enabled === false) return null;

		// ⚠️ SET SYNCHRONOUSLY, BEFORE THE FIRST `await`. Two calls in one tick both reach this line
		// before either yields, so the second must see the flag already raised. DROPPED, not queued.
		if (inFlightRef.current) return null;

		const transcripts = input.transcripts;
		if (!Array.isArray(transcripts) || transcripts.length < MIN_TRANSCRIPTS_FOR_SUGGESTIONS) return null;

		if (!input.patient?.id) return null;

		inFlightRef.current = true;

		try {
			const existingSuggestions = input.suggestions || [];
			const recentTranscripts = getRelevantTranscripts(transcripts);
			const stage: StageArgs = { patient: input.patient, patientId: input.patient?.id ?? null, roomId };

			let hasChanges = false;

			// STEP 1 & 2, IN PARALLEL — the update check and the cooldown read. `shouldRunTriage` is
			// synchronous and is evaluated here, before the await, exactly as the JS did.
			const [updatedSuggestions, shouldGenerate] = await Promise.all([
				checkSuggestionUpdates({ ...stage, transcripts: recentTranscripts, existingSuggestions }),
				Promise.resolve(shouldRunTriage(roomId)),
			]);

			if (updatedSuggestions.length > 0) hasChanges = true;

			// Cooldown active — apply the updates and stop. No triage, no generation.
			if (!shouldGenerate) {
				return { suggestions: mergeSuggestions(existingSuggestions, updatedSuggestions, []), hasChanges };
			}

			// The triage reads the set the updates have already been folded into.
			const currentSuggestions = mergeSuggestions(existingSuggestions, updatedSuggestions, []);

			// STEP 3 — triage.
			const triage = await generateTriage({ ...stage, transcripts: recentTranscripts, activeSuggestions: currentSuggestions });

			// The cooldown starts when the triage LANDS, as it did in talk.
			triageCacheRef.current.set(roomId, Date.now());

			if (!triage?.run_analyzers) {
				return { suggestions: mergeSuggestions(existingSuggestions, updatedSuggestions, []), hasChanges };
			}

			// STEP 4 — the six-way fan-out, in parallel, gated by the triage.
			const results = await Promise.all(
				SUGGESTION_TYPES.filter((type) => triage.run_analyzers[`${type}_suggestions`]).map(async (type) => {
					try {
						const suggestions = await generateSpecializedSuggestions({
							...stage,
							transcripts: recentTranscripts,
							existingSuggestions: currentSuggestions,
							suggestionType: type,
							consultationNotes: input.consultationNotes,
							consultParticulars: input.consultParticulars,
						});
						return { type, suggestions };
					} catch {
						// A rejection here would take the whole `Promise.all` down and lose the other
						// five branches with it.
						console.error("consult.suggestions.generate.failed", { roomId, suggestionType: type });
						return { type, suggestions: [] as Suggestion[] };
					}
				}),
			);

			const allNewSuggestions: Suggestion[] = [];
			results.forEach(({ suggestions }) => {
				if (suggestions.length > 0) {
					allNewSuggestions.push(...suggestions);
					hasChanges = true;
				}
			});

			// STEP 5 — merge everything. NEVER LOSE ANY.
			return { suggestions: mergeSuggestions(existingSuggestions, updatedSuggestions, allNewSuggestions), hasChanges };
		} catch {
			// The room keeps whatever it already had. `run()` never throwing is the contract the
			// caller relies on to keep the consult usable when a pass goes wrong.
			console.error("consult.suggestions.round.failed", { roomId });
			return { suggestions: optionsRef.current.suggestions || [], hasChanges: false };
		} finally {
			inFlightRef.current = false;
		}
	}, [shouldRunTriage]);

	return { runRound, isRunning };
}
